import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSWRImmutable from 'swr/immutable';

import { BonusOrigin, BonusType, CMSBonus, normalizeBonuses } from '@/api/cms';
import { correctLang } from '@/api/cms/base';
import { filterByGroups } from '@/helpers/cms';
import { fetcherCMS } from '@/modules/api';
import {
  CMSSortTypeEnum,
  createCMSQuery,
  DEFAULT_CMS_PAGE_SIZE,
  getCMSDurationQuery,
  getCMSImageFieldsUrl,
  getCMSSchedulerQuery,
  getCMSShowGuestQuery,
} from '@/modules/cms';
import { useCommonStore } from '@/store';

export const useCMSSportsBonuses = () => {
  const {
    i18n: { language },
  } = useTranslation();

  const user = useCommonStore.use.user();
  const isAuthenticated = useCommonStore.use.isAuthenticated();

  const query = useMemo(() => {
    return createCMSQuery({
      sort: [`order:${CMSSortTypeEnum.ASC}`],
      filters: {
        $and: [
          getCMSSchedulerQuery(),
          getCMSDurationQuery(),
          { promo_page_toggle: { $eq: true } },
          { type_offer: { $eq: BonusType.SPORTS } },
          ...getCMSShowGuestQuery(isAuthenticated),
        ],
      },
      populate: {
        scheduler: '*',
        ...getCMSImageFieldsUrl('image'),
      },
      locale: [correctLang(language)],
      pagination: {
        pageSize: DEFAULT_CMS_PAGE_SIZE,
      },
    });
  }, [language, isAuthenticated]);

  const {
    data = [],
    error,
    isLoading,
  } = useSWRImmutable<BonusOrigin[]>(`/bonuses-news?${query}`, fetcherCMS);

  const bonuses = useMemo(() => {
    if (!data.length) return [];
    // TODO: fix typo in CMS to avoid normalizeBonuses
    const normalizedBonuses = normalizeBonuses(data);
    // TODO: optimize logic to avoid client-side filterByGroups
    return filterByGroups<CMSBonus>({ user })(normalizedBonuses);
  }, [data, user]);

  return {
    data,
    isLoading,
    isError: error,
    bonuses,
  };
};
